// Pricing.js (Products Pricing Page Component)

import React from 'react';
import styled from 'styled-components';
import { GiCutDiamond, GiIdCard } from 'react-icons/gi';
import { IoIosPaper, IoIosBusiness, IoIosCart, IoIosArrowForward } from "react-icons/io";
import { FaFileAlt } from 'react-icons/fa';
import { IconContext } from 'react-icons/lib';

const PageWrapper = styled.div`
  background: radial-gradient(circle at 50% 0%, #151a2e 0%, #0a0d16 100%);
  color: #e2e8f0;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  padding: 80px 20px 100px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Heading = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 3rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #ffffff 30%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ProductCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 35px;
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 520px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #4b59f7, #0467fb);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(75, 89, 247, 0.3);
    background: rgba(255, 255, 255, 0.035);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);

    &::before {
      opacity: 1;
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  border-radius: 16px;
  background: rgba(75, 89, 247, 0.1);
  border: 1px solid rgba(75, 89, 247, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #818cf8;
  margin-bottom: 25px;
  transition: all 0.3s ease;

  ${ProductCard}:hover & {
    background: rgba(75, 89, 247, 0.2);
    color: #a5b4fc;
    transform: scale(1.05);
  }
`;

const PlanTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
`;

const CategoryTag = styled.span`
  align-self: flex-start;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #818cf8;
  background: rgba(75, 89, 247, 0.12);
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 25px;
  min-height: 75px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 25px;
`;

const FeatureItem = styled.li`
  font-size: 0.9rem;
  color: #cbd5e1;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '✓';
    color: #10b981;
    font-weight: 700;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #4b59f7 0%, #0467fb 100%);
  color: #fff;
  border: none;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(75, 89, 247, 0.25);
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(75, 89, 247, 0.4);
    background: linear-gradient(135deg, #5c6aff 0%, #1e7aff 100%);
  }

  &:active {
    transform: translateY(0);
  }
`;

function Pricing() {
  return (
    <IconContext.Provider value={{ size: '28px' }}>
      <PageWrapper>
        <ContentContainer>
          <HeaderSection>
            <Heading>Our Products</Heading>
            <Subtitle>
              Explore our range of innovative digital profiles, gaming applications, and interactive portals.
            </Subtitle>
          </HeaderSection>

          <Grid>
            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <GiIdCard />
                </IconWrapper>
                <PlanTitle>Zangie Card</PlanTitle>
                <CategoryTag>Digital Cards</CategoryTag>
                <Description>
                  Turn paper cards into smart digital contacts. Share your professional details instantly across any device.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Cloud Contacts Sync</FeatureItem>
                  <FeatureItem>Message Customers Instantly</FeatureItem>
                  <FeatureItem>Cross-Platform Smart Sharing</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href="/Blog/#zangie"}>
                  View Product Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <FaFileAlt />
                </IconWrapper>
                <PlanTitle>ATS Resume Flow</PlanTitle>
                <CategoryTag>AI Job Optimizer</CategoryTag>
                <Description>
                  Beat Applicant Tracking Systems with professional AI. Analyze keyword gaps, get instant ATS matching scores, and generate job-tailored resumes.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>AI Score & Keyword Analysis</FeatureItem>
                  <FeatureItem>Job-Tailored Resume Generator</FeatureItem>
                  <FeatureItem>ATS-Friendly PDF Templates</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.open('https://atsresumeflow.com/')}>
                  Optimize Now <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <GiCutDiamond />
                </IconWrapper>
                <PlanTitle>Puzzle Block Color</PlanTitle>
                <CategoryTag>Game App</CategoryTag>
                <Description>
                  Dive into color-matching block challenges. The ultimate brain-teaser that tests strategy, speed, and reflexes.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Strategic Brain Teasers</FeatureItem>
                  <FeatureItem>Perfect for All Ages</FeatureItem>
                  <FeatureItem>Dynamic Interactive Gameplay</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mounts.game')}>
                  Download Now <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>
          </Grid>

          <HeaderSection style={{ marginTop: '40px' }}>
            <Heading>Software & Business Tools</Heading>
            <Subtitle>
              Maximize operational efficiency and streamline workflows with our tailored business systems.
            </Subtitle>
          </HeaderSection>

          <Grid>
            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoIosBusiness />
                </IconWrapper>
                <PlanTitle>Hotel Management System</PlanTitle>
                <CategoryTag>HMS Utility</CategoryTag>
                <Description>
                  Manage front-desk check-ins, reservations, inventory, housekeeping, rate occupancy, and secure payment processing.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Front-Desk Operations Scheduler</FeatureItem>
                  <FeatureItem>Customized PDF Reports & Billing</FeatureItem>
                  <FeatureItem>Integrated Expenses Tracker</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href = "/Blog/#Hotel"}>
                  View Product Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoIosPaper />
                </IconWrapper>
                <PlanTitle>Billing Software</PlanTitle>
                <CategoryTag>Automation Tool</CategoryTag>
                <Description>
                  Automate your invoicing process, manage client databases, track outstanding bills, and receive payment alerts.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Client & Service Database</FeatureItem>
                  <FeatureItem>Late Payment Auto-Reminders</FeatureItem>
                  <FeatureItem>Advanced Reporting & Analytics</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href = "/Blog/#Billing"}>
                  View Product Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>

            <ProductCard>
              <CardTop>
                <IconWrapper>
                  <IoIosCart />
                </IconWrapper>
                <PlanTitle>Vendor Management System</PlanTitle>
                <CategoryTag>VMS Utility</CategoryTag>
                <Description>
                  Streamline supplier relationships, handle vendor onboarding, manage orders/contracts, and monitor warehouse inventory.
                </Description>
              </CardTop>
              <div>
                <FeatureList>
                  <FeatureItem>Easy Vendor Onboarding System</FeatureItem>
                  <FeatureItem>Contract & Order Trackers</FeatureItem>
                  <FeatureItem>Warehouse Inventory Management</FeatureItem>
                </FeatureList>
                <ActionButton onClick={() => window.location.href = "/Blog/#Vendor"}>
                  View Product Details <IoIosArrowForward />
                </ActionButton>
              </div>
            </ProductCard>
          </Grid>
        </ContentContainer>
      </PageWrapper>
    </IconContext.Provider>
  );
}

export default Pricing;
